import React, { useState } from 'react'
import { SignableCreditApplicationDocumentMeta, UnderwritingOffer } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import useDeviceDetect from '@sputnik/hooks/useDeviceDetect'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from '@sputnik/pages/gql/GetCreditApplicationTilaInfo.gql'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useAcceptTilaAgreement } from './AcceptTilaAgreement.gql'
import AgreementLink from './AgreementLink'
import AgreementModal from './AgreementModal'
import AgreementPopsheet from './AgreementPopsheet'
import ApplicationTerms from './ApplicationTerms'
import styles from './styles.module.scss'

export interface ApplicationAgreementsProps {
  creditApplication: CreditApplicationType
  hasAgreedToTerms: boolean
  offerType?: UnderwritingOffer | null
  decisionId?: SignableCreditApplicationDocumentMeta['underwritingDecisionUuid']
  hasAgreedToTila: boolean
  onCheckboxClick: (e: React.ChangeEvent) => void
}

const ApplicationAgreements = ({
  creditApplication,
  hasAgreedToTerms,
  hasAgreedToTila,
  offerType,
  decisionId,
  onCheckboxClick,
}: ApplicationAgreementsProps) => {
  const { isMobile } = useDeviceDetect()
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const [getClassName, makeTestId] = useNameFormatter('ApplicationAgreements')
  const [isShowingAgreementModal, setIsShowingAgreementModal] = useState(false)
  const { tila, salesforceCreditId } = creditApplication || {}
  const needsTilaAgreement = !!tila
  const tilaContent = tila?.content
  const disableApplicationTerms = needsTilaAgreement && !hasAgreedToTila

  const [acceptTilaAgreement, { loading: isAcceptingTilaAgreement }] = useAcceptTilaAgreement({
    onCompleted: () => {
      setIsShowingAgreementModal(false)

      logInfo('acceptTilaAgreement', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'accepts agreement terms for application',
        },
        eventName: 'applicationAgreements',
      })

      showSuccessToast(
        <FormattedMessage
          defaultMessage='You have successfully agreed to your offer summary'
          id='sputnik.ApplicationAgreements__VFXi23'
        />,
      )
    },
    onError: (e) => {
      setIsShowingAgreementModal(false)

      logError(e, {
        attributes: {
          creditAppId: salesforceCreditId,
          action: 'acceptTilaAgreement',
          message: e?.message,
          result: 'Error accepting agreement terms for application',
        },
        eventName: 'applicationAgreements',
      })

      showDangerToast(
        <FormattedMessage
          defaultMessage='There was an error agreeing to your offer summary.'
          id='sputnik.ApplicationAgreements__9fJ3od'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const handleAgreeToOfferSummary = () => {
    if (offerType) {
      acceptTilaAgreement({
        variables: {
          salesforceCreditId: salesforceCreditId as string,
          underwritingDecisionUuid: decisionId,
          underwritingOfferType: offerType,
        },
        refetchQueries: ['GetTilaInfo'],
      })
    }
  }

  return (
    <div
      className={`${styles[getClassName('')]} fs-unmask`}
      data-testid={makeTestId('')}
    >
      {needsTilaAgreement && (
        <AgreementLink
          hasAgreedToTila={hasAgreedToTila}
          onClick={() => setIsShowingAgreementModal(true)}
        />
      )}
      <ApplicationTerms
        creditApplication={creditApplication}
        disableApplicationTerms={disableApplicationTerms}
        hasAgreedToTerms={hasAgreedToTerms}
        onCheckboxClick={onCheckboxClick}
      />
      {!isMobile && tilaContent && offerType && (
        <AgreementModal
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isShowing={isShowingAgreementModal}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={() => setIsShowingAgreementModal(false)}
        />
      )}
      {isMobile && tilaContent && offerType && (
        <AgreementPopsheet
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isShowing={isShowingAgreementModal}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={() => setIsShowingAgreementModal(false)}
        />
      )}
    </div>
  )
}

export default ApplicationAgreements
